import React, { useState, useEffect } from 'react';
import { Modal, Paper, Typography, Button, Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function playTestSound() {
  const AudioCtx = globalThis.window.AudioContext || (globalThis.window as any).webkitAudioContext;
  const ctx = new AudioCtx();

  const oscillator = ctx.createOscillator();
  oscillator.type = "sine";
  oscillator.frequency.value = 1000;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.15, ctx.currentTime);
  gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 2.5);

  oscillator.connect(gain);
  gain.connect(ctx.destination);

  oscillator.start();
  oscillator.stop(ctx.currentTime + 3);

  oscillator.onended = () => ctx.close();
}

interface PreAssessmentModalProps {
  open: boolean;
  onVerified: () => void;
}

const PreAssessmentModal: React.FC<PreAssessmentModalProps> = ({ open, onVerified }) => {
  const [micWorking, setMicWorking] = useState(false);
  const [soundHeard, setSoundHeard] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const { refreshAccessToken } = useAuth();

  // Mic test: Try to getUserMedia and detect quick sound level
  useEffect(() => {
    if (!open) return;
    let stream: MediaStream | undefined;
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((s: MediaStream) => {
        stream = s;
        setMicWorking(true);
      })
      .catch(() => setMicWorking(false));
    return () => {
      stream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
    };
  }, [open]);

  const handleStartAssessment = async () => {
    setRefreshing(true);
    try {
      await refreshAccessToken();
      console.log('[Token] Refreshed')
    } catch (e) {
      console.log(`MeetingPreAssessmentModal: Error while attempting token refresh - ${e}`);
    }
    setRefreshing(false);
    onVerified();
  };

  return (
    <Modal open={open}>
      <Paper sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)', p: 4, minWidth: 400
      }}>
        <Typography
          variant="h4"
          gutterBottom
          align="center"
          sx={{
            textDecoration: "underline",
            width: "100%",
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            textAlign: "center"
          }}
        >
          Pre-Assessment Setup
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography
            // variant="h6"
            gutterBottom
            align="center"
            sx={{
              width: "100%",
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
              textAlign: "center"
            }}>
            Please verify your microphone and audio are working before starting the assessment.
            <br/>
            <br/>
            Notice!  Make sure to keep your computer awake, if it falls asleep the session may be canceled.
            <br></br>
            We recommend moving the cursor every couple of minutes.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignContent: "center", justifyContent: "center" }}>
          <Typography>Microphone Test:</Typography>
          <Box sx={{width: 16}}></Box>
          {micWorking ? (
            <Typography color="success.main">Mic detected!</Typography>
          ) : (
            <Typography color="error.main">Mic not working. Please allow access.</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
          {/* First row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={playTestSound}
              sx={{ flex: 1 }}
            >
              Play Test Sound
            </Button>
            <Button
              variant="contained"
              color={soundHeard ? "success" : "primary"}
              onClick={() => setSoundHeard(true)}
              disabled={!micWorking}
              sx={{ flex: 1 }}
            >
              I Heard the Sound
            </Button>
          </Box>
          {/* Second row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartAssessment}
              disabled={!(micWorking && soundHeard) || refreshing}
              sx={
                !(micWorking && soundHeard) || refreshing
                  ? {
                      flex: 1,
                      background: 'transparent',
                    }
                  : { 
                      flex: 1,
                      background: 'linear-gradient(45deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
                      color: 'white'
                   }
                }
              >
              {refreshing ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />Loading...
                </>
              ) : (
                "Start Assessment"
              )}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => navigate('/assessment')}
              sx={{ 
                flex: 1,
                background: 'linear-gradient(45deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
                color: 'white'
               }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default PreAssessmentModal;